import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, MoreVertical, ChevronLeft, Users, DollarSign, TrendingUp, Edit2, Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../services/api';

const Rosters = () => {
  // State
  const [positionFilter, setPositionFilter] = useState('all');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [newPlayer, setNewPlayer] = useState({
    name: '',
    position: 'Forward',
    positionAbbr: 'SF',
    team: '',
    teamAbbr: '',
    country: 'USA',
    image: '',
    matchesPlayed: 0,
    totalMatches: 82,
    pointsPerGame: 0,
    reboundsPerGame: 0,
    assistsPerGame: 0,
    contractValue: '$0M',
    contractLength: '1 year',
    category: 'forwards'
  });

  // Position filters
  const positions = [
    { id: 'all', label: 'All Players' },
    { id: 'guards', label: 'Guards' },
    { id: 'forwards', label: 'Forwards' },
    { id: 'centers', label: 'Centers' },
  ];

  // Fetch players from API
  const fetchPlayers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiCall('/rosters/players');
      if (response.success) {
        setPlayers(response.players || []);
        if (response.players?.length > 0 && !selectedPlayer) {
          setSelectedPlayer(response.players[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching players:', error);
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, [selectedPlayer]);

  useEffect(() => {
    fetchPlayers();
  }, []);

  // Add player
  const handleAddPlayer = async () => {
    if (!newPlayer.name) {
      toast.error('Player name is required');
      return;
    }

    try {
      const response = await apiCall('/rosters/players', {
        method: 'POST',
        body: JSON.stringify(newPlayer)
      });

      if (response.success) {
        setPlayers(prev => [response.player, ...prev]);
        setShowAddModal(false);
        resetPlayerForm();
        toast.success('Player added to roster!');
      }
    } catch (error) {
      toast.error('Failed to add player');
    }
  };

  // Edit player
  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setNewPlayer({
      name: player.name || '',
      position: player.position || 'Forward',
      positionAbbr: player.positionAbbr || 'SF',
      team: player.team || '',
      teamAbbr: player.teamAbbr || '',
      country: player.country || 'USA',
      image: player.image || '',
      matchesPlayed: player.matchesPlayed || 0,
      totalMatches: player.totalMatches || 82,
      pointsPerGame: player.pointsPerGame || 0,
      reboundsPerGame: player.reboundsPerGame || 0,
      assistsPerGame: player.assistsPerGame || 0,
      contractValue: player.contractValue || '$0M',
      contractLength: player.contractLength || '1 year',
      category: player.category || 'forwards'
    });
    setShowAddModal(true);
  };

  // Update player
  const handleUpdatePlayer = async () => {
    if (!newPlayer.name) {
      toast.error('Player name is required');
      return;
    }

    try {
      const response = await apiCall(`/rosters/players/${editingPlayer.id}`, {
        method: 'PUT',
        body: JSON.stringify(newPlayer)
      });

      if (response.success) {
        setPlayers(prev => prev.map(p => 
          p.id === editingPlayer.id ? response.player : p
        ));
        if (selectedPlayer?.id === editingPlayer.id) {
          setSelectedPlayer(response.player);
        }
        setShowAddModal(false);
        setEditingPlayer(null);
        resetPlayerForm();
        toast.success('Player updated!');
      }
    } catch (error) {
      toast.error('Failed to update player');
    }
  };

  // Delete player
  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm('Remove this player from roster?')) return;

    try {
      const response = await apiCall(`/rosters/players/${playerId}`, {
        method: 'DELETE'
      });

      if (response.success) {
        setPlayers(prev => prev.filter(p => p.id !== playerId));
        if (selectedPlayer?.id === playerId) {
          setSelectedPlayer(players.find(p => p.id !== playerId) || null);
        }
        toast.success('Player removed from roster');
      }
    } catch (error) {
      toast.error('Failed to remove player');
    }
  };

  // Reset form
  const resetPlayerForm = () => {
    setNewPlayer({
      name: '',
      position: 'Forward',
      positionAbbr: 'SF',
      team: '',
      teamAbbr: '',
      country: 'USA',
      image: '',
      matchesPlayed: 0,
      totalMatches: 82,
      pointsPerGame: 0,
      reboundsPerGame: 0,
      assistsPerGame: 0,
      contractValue: '$0M',
      contractLength: '1 year',
      category: 'forwards'
    });
  };

  // Close modal
  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingPlayer(null);
    resetPlayerForm();
  };

  // Filter players
  const filteredPlayers = positionFilter === 'all' 
    ? players 
    : players.filter(p => p.category === positionFilter);

  // Calculate team stats
  const totalPlayers = players.length;
  const maxPlayers = 20;
  const totalCost = players.reduce((sum, p) => {
    const value = parseFloat((p.contractValue || '$0M').replace('$', '').replace('M', ''));
    return sum + (isNaN(value) ? 0 : value);
  }, 0);

  // Team color mapping
  const teamColors = {
    'LAL': 'bg-purple-600',
    'GSW': 'bg-blue-500',
    'PHX': 'bg-orange-500',
    'MIL': 'bg-green-600',
    'DAL': 'bg-blue-700',
    'DEN': 'bg-yellow-600',
    'PHI': 'bg-blue-600',
    'BOS': 'bg-green-700',
  };

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white">Rosters</h1>
          <p className="text-gray-400 mt-1">Team Roster Management</p>
        </div>

        <div className="flex gap-6">
          {/* Main Content */}
          <div className="flex-1">
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {/* Players Count */}
              <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Players</span>
                  <button className="text-gray-400 hover:text-white">
                    <MoreVertical size={18} />
                  </button>
                </div>
                <p className="text-4xl font-bold text-white">
                  {totalPlayers}<span className="text-gray-500">/{maxPlayers}</span>
                </p>
              </div>

              {/* Team Cost */}
              <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Team Cost</span>
                  <button className="text-gray-400 hover:text-white">
                    <MoreVertical size={18} />
                  </button>
                </div>
                <p className="text-4xl font-bold text-white">${totalCost.toFixed(1)}M</p>
              </div>

              {/* Add Player */}
              <button 
                onClick={() => {
                  toast.info('Add Player functionality coming soon');
                  setShowAddModal(true);
                }}
                className="bg-orange-500 hover:bg-orange-600 rounded-xl p-5 flex flex-col items-center justify-center transition-colors"
              >
                <Plus size={32} className="text-white mb-2" />
                <span className="text-white font-semibold">Add Player</span>
              </button>
            </div>

            {/* Position Filter */}
            <div className="flex gap-2 mb-6">
              {positions.map(pos => (
                <button
                  key={pos.id}
                  onClick={() => setPositionFilter(pos.id)}
                  className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    positionFilter === pos.id 
                      ? 'bg-white text-black' 
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {pos.label}
                </button>
              ))}
            </div>

            {/* Players Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {loading ? (
                <div className="col-span-full flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredPlayers.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-400">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No players found</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="mt-4 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                  >
                    Add First Player
                  </button>
                </div>
              ) : (
                filteredPlayers.map(player => (
                <div 
                  key={player.id}
                  className={`bg-black border rounded-xl overflow-hidden cursor-pointer transition-all hover:border-gray-600 relative group ${
                    selectedPlayer?.id === player.id ? 'border-orange-500' : 'border-gray-800'
                  }`}
                >
                  {/* Action Buttons - Show on hover */}
                  <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEditPlayer(player); }}
                      className="p-2 bg-black/80 hover:bg-orange-500 text-white rounded-lg transition-colors"
                      data-testid={`edit-player-${player.id}`}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeletePlayer(player.id); }}
                      className="p-2 bg-black/80 hover:bg-red-500 text-white rounded-lg transition-colors"
                      data-testid={`delete-player-${player.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  
                  {/* Player Image */}
                  <div 
                    className="aspect-square overflow-hidden"
                    onClick={() => setSelectedPlayer(player)}
                  >
                    <img 
                      src={player.image} 
                      alt={player.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(player.name)}&size=400&background=1a1a2e&color=fff`;
                      }}
                    />
                  </div>
                  
                  {/* Player Info */}
                  <div className="p-4" onClick={() => setSelectedPlayer(player)}>
                    <h3 className="text-white font-semibold">{player.name}</h3>
                    <p className="text-gray-400 text-sm">{player.position}, {player.positionAbbr}</p>
                  </div>
                </div>
              )))}
            </div>
          </div>

          {/* Right Sidebar - Player Details */}
          {selectedPlayer && (
            <div className="w-80 flex-shrink-0 space-y-4">
              {/* Back Button */}
              <button 
                onClick={() => setSelectedPlayer(null)}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <ChevronLeft size={18} />
                <span>Back to team</span>
              </button>

              {/* Player Card */}
              <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 overflow-hidden">
                {/* Player Image */}
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={selectedPlayer.image} 
                    alt={selectedPlayer.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedPlayer.name)}&size=400&background=1a1a2e&color=fff`;
                    }}
                  />
                </div>

                {/* Player Info */}
                <div className="p-4">
                  <h2 className="text-2xl font-bold text-white mb-1">{selectedPlayer.name}</h2>
                  <p className="text-gray-400 mb-4">{selectedPlayer.position}, {selectedPlayer.positionAbbr}</p>

                  {/* Matches/Goals */}
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-3xl font-bold text-white">
                      {selectedPlayer.matchesPlayed}/{selectedPlayer.totalMatches}
                    </span>
                    <span className="text-gray-400 text-sm">matches/goals</span>
                  </div>

                  {/* Team Badge */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-8 h-8 ${teamColors[selectedPlayer.teamAbbr] || 'bg-gray-600'} rounded-full flex items-center justify-center text-white text-xs font-bold`}>
                      {selectedPlayer.teamAbbr}
                    </div>
                    <span className="text-white">{selectedPlayer.team}</span>
                    <span className="text-gray-400 ml-auto">🇺🇸 {selectedPlayer.country}</span>
                  </div>

                  {/* Stats Table */}
                  <div className="border-t border-gray-800 pt-4 space-y-3">
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-400 text-sm uppercase">Added to Team</span>
                      <span className="text-white">{selectedPlayer.addedToTeam}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-400 text-sm">Points Per Game</span>
                      <span className="text-white font-semibold">{selectedPlayer.pointsPerGame}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-400 text-sm">Rebounds Per Game</span>
                      <span className="text-white font-semibold">{selectedPlayer.reboundsPerGame}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-400 text-sm">Assists Per Game</span>
                      <span className="text-white font-semibold">{selectedPlayer.assistsPerGame}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-400 text-sm">Contract Value</span>
                      <span className="text-white font-semibold">{selectedPlayer.contractValue}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-400 text-sm">Contract Length</span>
                      <span className="text-white font-semibold">{selectedPlayer.contractLength}</span>
                    </div>
                  </div>

                  {/* Full Statistics Button */}
                  <button className="w-full mt-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors">
                    Full statistics →
                  </button>
                </div>
              </div>

              {/* Cost Chart */}
              <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold">Cost chart</h3>
                  <span className="text-gray-400 text-sm">Peak: $528.4M</span>
                </div>
                
                {/* Simple chart visualization */}
                <div className="relative h-24">
                  <svg className="w-full h-full" viewBox="0 0 200 80">
                    <defs>
                      <linearGradient id="chartGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#06b6d4" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M 0 60 Q 30 55 50 50 T 80 35 T 120 45 T 150 25 T 180 30 T 200 20"
                      fill="none"
                      stroke="url(#chartGradient)"
                      strokeWidth="3"
                    />
                    {/* Data points */}
                    {[0, 50, 80, 120, 150, 180, 200].map((x, i) => (
                      <circle 
                        key={i}
                        cx={x} 
                        cy={[60, 50, 35, 45, 25, 30, 20][i]} 
                        r="4" 
                        fill="#3b82f6"
                      />
                    ))}
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Player Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editingPlayer ? 'Edit Player' : 'Add New Player'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-800 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Player Name *</label>
                <input
                  type="text"
                  value={newPlayer.name}
                  onChange={(e) => setNewPlayer(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="e.g., LeBron James"
                />
              </div>
              
              {/* Position & Abbreviation */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Position</label>
                  <select
                    value={newPlayer.position}
                    onChange={(e) => {
                      const pos = e.target.value;
                      const abbr = pos === 'Guard' ? 'PG' : pos === 'Center' ? 'C' : 'SF';
                      const cat = pos === 'Guard' ? 'guards' : pos === 'Center' ? 'centers' : 'forwards';
                      setNewPlayer(prev => ({ ...prev, position: pos, positionAbbr: abbr, category: cat }));
                    }}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  >
                    <option value="Guard">Guard</option>
                    <option value="Forward">Forward</option>
                    <option value="Center">Center</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Position Abbr</label>
                  <input
                    type="text"
                    value={newPlayer.positionAbbr}
                    onChange={(e) => setNewPlayer(prev => ({ ...prev, positionAbbr: e.target.value }))}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                    placeholder="e.g., SF/PF"
                  />
                </div>
              </div>
              
              {/* Team & Team Abbr */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Team</label>
                  <input
                    type="text"
                    value={newPlayer.team}
                    onChange={(e) => setNewPlayer(prev => ({ ...prev, team: e.target.value }))}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                    placeholder="e.g., Los Angeles Lakers"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Team Abbr</label>
                  <input
                    type="text"
                    value={newPlayer.teamAbbr}
                    onChange={(e) => setNewPlayer(prev => ({ ...prev, teamAbbr: e.target.value }))}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                    placeholder="e.g., LAL"
                  />
                </div>
              </div>
              
              {/* Country */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Country</label>
                <input
                  type="text"
                  value={newPlayer.country}
                  onChange={(e) => setNewPlayer(prev => ({ ...prev, country: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="e.g., USA"
                />
              </div>
              
              {/* Image URL */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Image URL</label>
                <input
                  type="url"
                  value={newPlayer.image}
                  onChange={(e) => setNewPlayer(prev => ({ ...prev, image: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="https://..."
                />
              </div>
              
              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">PPG</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newPlayer.pointsPerGame}
                    onChange={(e) => setNewPlayer(prev => ({ ...prev, pointsPerGame: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">RPG</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newPlayer.reboundsPerGame}
                    onChange={(e) => setNewPlayer(prev => ({ ...prev, reboundsPerGame: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">APG</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newPlayer.assistsPerGame}
                    onChange={(e) => setNewPlayer(prev => ({ ...prev, assistsPerGame: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  />
                </div>
              </div>
              
              {/* Games Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Matches Played</label>
                  <input
                    type="number"
                    value={newPlayer.matchesPlayed}
                    onChange={(e) => setNewPlayer(prev => ({ ...prev, matchesPlayed: parseInt(e.target.value) || 0 }))}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Total Matches</label>
                  <input
                    type="number"
                    value={newPlayer.totalMatches}
                    onChange={(e) => setNewPlayer(prev => ({ ...prev, totalMatches: parseInt(e.target.value) || 82 }))}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  />
                </div>
              </div>
              
              {/* Contract Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contract Value</label>
                  <input
                    type="text"
                    value={newPlayer.contractValue}
                    onChange={(e) => setNewPlayer(prev => ({ ...prev, contractValue: e.target.value }))}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                    placeholder="e.g., $44.5M"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contract Length</label>
                  <input
                    type="text"
                    value={newPlayer.contractLength}
                    onChange={(e) => setNewPlayer(prev => ({ ...prev, contractLength: e.target.value }))}
                    className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                    placeholder="e.g., 2 years"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={handleCloseModal}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={editingPlayer ? handleUpdatePlayer : handleAddPlayer}
                className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
                data-testid="save-player-btn"
              >
                {editingPlayer ? 'Save Changes' : 'Add Player'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rosters;
